import { useEffect, useState } from "react";
import { CloudSlash, CloudArrowUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  PENDING_EVENT, pendingHarvests, setSyncTenant, startAutoSync, syncPendingHarvests,
} from "@/lib/offline";

// Thin strip above the page: says when the app is running on saved data
// with no network, and how many harvest lots are still waiting on this
// device to upload. Also owns the background auto-upload (see lib/offline.js)
// for as long as someone is logged in.
export default function OfflineBar({ tenantSlug }) {
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  const [pending, setPending] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSyncTenant(tenantSlug);
    const refresh = () => setPending(pendingHarvests().length);
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    refresh();
    window.addEventListener(PENDING_EVENT, refresh);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    const stop = startAutoSync((n) => toast.success(`Uploaded ${n} harvest lot${n === 1 ? "" : "s"} saved offline`));
    return () => {
      stop();
      window.removeEventListener(PENDING_EVENT, refresh);
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [tenantSlug]);

  const uploadNow = async () => {
    setBusy(true);
    try {
      const { synced, remaining } = await syncPendingHarvests();
      if (synced > 0) toast.success(`Uploaded ${synced} harvest lot${synced === 1 ? "" : "s"}`);
      if (remaining > 0) toast.error(`${remaining} lot${remaining === 1 ? "" : "s"} could not upload yet — will keep trying`);
    } finally {
      setBusy(false);
    }
  };

  if (online && pending === 0) return null;

  const lots = `${pending} harvest lot${pending === 1 ? "" : "s"}`;
  return (
    <div data-testid="offline-bar"
      className={`flex items-center gap-2 px-4 py-2 text-xs font-medium ${online ? "bg-sky-50 text-sky-800 border-b border-sky-200" : "bg-amber-100 text-amber-900 border-b border-amber-300"}`}>
      {online ? <CloudArrowUp size={16} weight="bold" /> : <CloudSlash size={16} weight="bold" />}
      <span className="flex-1">
        {online
          ? `${lots} saved on this device, waiting to upload`
          : `No internet — showing last saved data. New harvest lots are kept on this device${pending ? ` (${lots} waiting)` : ""} and upload automatically when back online.`}
      </span>
      {online && (
        <button data-testid="btn-upload-pending" disabled={busy} onClick={uploadNow}
          className="shrink-0 rounded bg-sky-700 px-2 py-1 text-white hover:bg-sky-800 disabled:opacity-60">
          {busy ? "Uploading…" : "Upload now"}
        </button>
      )}
    </div>
  );
}
